import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { Reveal } from "@/components/motion/Reveal";

const COURSES = [
  ["patent-analytics", "Patent Analytics"],
  ["patent-drafting", "Patent Drafting"],
  ["trademark-search", "Trademark Search"],
  ["ip-strategy", "IP Strategy"],
  ["patent-valuation", "Patent Valuation"],
  ["technology-transfer", "Technology Transfer"],
  ["introduction-to-ip-law", "Introduction to IP Law and Indian Patent Law"],
  ["indian-trademark-agent-exam", "How to ace the Indian Trademark Agent Exam"],
  ["certified-patent-valuation-analyst", "Guide to become CPVA"],
  ["ip-commercialisation", "Guide to IP Commercialisation"],
];

const inputCls =
  "w-full h-11 px-4 bg-transparent border border-[#C6A87C]/25 text-[#F5F4F0] placeholder:text-[#8A99AA]/50 focus:border-[#C6A87C] outline-none transition-colors duration-300 text-sm";

export default function EnrollmentForm({ defaultCourse = "" }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    course_slug: defaultCourse,
    background: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (v) =>
    setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.course_slug) {
      toast.error("Please fill name, email, phone and select a course.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enrollments", form);
      toast.success("Enrolment request received. We'll be in touch within 24 hours.");
      setSubmitted(true);
      setForm({
        name: "",
        email: "",
        phone: "",
        course_slug: defaultCourse,
        background: "",
        message: "",
      });
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div
        data-testid="enrollment-success"
        className="border border-[#C6A87C]/50 bg-navy-deep p-10 text-center"
      >
        <div className="label-overline mb-3">Confirmed</div>
        <h3 className="text-3xl text-[#F5F4F0] mb-3 font-serif">Your request is in.</h3>
        <p className="text-[#8A99AA] font-light max-w-md mx-auto">
          A programme advisor from Gurukul of Ramana will write to you within one
          business day with the cohort calendar and prerequisites.
        </p>
        <button
          data-testid="enrollment-submit-another"
          onClick={() => setSubmitted(false)}
          className="mt-6 h-11 px-8 bg-[#C6A87C] text-[#061324] text-xs font-semibold tracking-[0.14em] uppercase hover:bg-[#E2C69D] transition-colors duration-300"
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <Reveal>
      <form
        data-testid="enrollment-form"
        onSubmit={onSubmit}
        className="bg-navy-deep border border-[#C6A87C]/20 p-8 lg:p-10 space-y-6"
      >
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Full name" required>
            <input
              data-testid="enroll-name"
              value={form.name}
              onChange={update("name")}
              placeholder="e.g. Aanya Iyer"
              className={inputCls}
            />
          </Field>
          <Field label="Email" required>
            <input
              data-testid="enroll-email"
              type="email"
              value={form.email}
              onChange={update("email")}
              placeholder="you@example.com"
              className={inputCls}
            />
          </Field>
          <Field label="Phone" required>
            <input
              data-testid="enroll-phone"
              value={form.phone}
              onChange={update("phone")}
              placeholder="+91 ..."
              className={inputCls}
            />
          </Field>
          <Field label="Course of interest" required>
            <Select value={form.course_slug} onValueChange={update("course_slug")}>
              <SelectTrigger
                data-testid="enroll-course-select"
                className="w-full h-11 px-4 rounded-none bg-transparent border-[#C6A87C]/25 text-[#F5F4F0] focus:border-[#C6A87C] focus:ring-0 text-sm"
              >
                <SelectValue placeholder="Select a programme" />
              </SelectTrigger>
              <SelectContent className="bg-[#0B2038] border-[#C6A87C]/25 text-[#F5F4F0] rounded-none">
                {COURSES.map(([slug, title]) => (
                  <SelectItem
                    key={slug}
                    value={slug}
                    data-testid={`enroll-option-${slug}`}
                    className="focus:bg-[#C6A87C] focus:text-[#061324] cursor-pointer"
                  >
                    {title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <Field label="Your background (optional)">
          <input
            data-testid="enroll-background"
            value={form.background}
            onChange={update("background")}
            placeholder="e.g. Mechanical engineer, 5 yrs, India"
            className={inputCls}
          />
        </Field>

        <Field label="Anything else?">
          <textarea
            data-testid="enroll-message"
            value={form.message}
            onChange={update("message")}
            placeholder="Goals, timing constraints, questions..."
            rows={4}
            className={`${inputCls} h-auto py-3 resize-y`}
          />
        </Field>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
          <button
            data-testid="enroll-submit"
            type="submit"
            disabled={submitting}
            className="group relative inline-flex items-center gap-3 h-12 px-10 bg-[#0F9D6B] text-[#F5F4F0] text-xs font-semibold tracking-[0.16em] uppercase overflow-hidden disabled:opacity-50"
          >
            <span className="absolute inset-0 bg-[#14B981] scale-x-0 origin-left transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-x-100" />
            <span className="relative inline-flex items-center gap-2">
              {submitting ? "Sending..." : "Request enrolment"}
              <ArrowRight size={14} weight="bold" />
            </span>
          </button>
          <p className="text-xs text-[#8A99AA]/70 font-light">
            We respond within one business day. No payment required to request.
          </p>
        </div>
      </form>
    </Reveal>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <label className="block text-[11px] uppercase tracking-[0.18em] text-[#8A99AA] font-semibold">
        {label} {required && <span className="text-[#C6A87C]">*</span>}
      </label>
      {children}
    </div>
  );
}
